// ============================================================
// PIE — Strategies (las formas de alcanzar los goals)
// ============================================================
// El optimizador NUNCA asume una sola: corre TODAS las registradas
// y elige la mejor según los goals ponderados del tenant.
// Cada strategy es PURA: genera candidatos o declina ([]).
// ============================================================
import type {
  PresentationCandidate,
  PresentationContext,
  PresentationIntent,
  Strategy,
  SubjectFacts,
  TenantPieConfig,
} from '../types';
import { composeDecision } from '../../visual-engine/decision/composeDecision';
import { buildContentProfile } from '../../visual-engine/analysis/contentProfile';
import type { ContentProfile } from '../../visual-engine/types';

// ── Helper: reconstruye el perfil VCE desde facts (para baseline) ──
function toVceProfile(facts: SubjectFacts): ContentProfile {
  return {
    mediaCount: facts.mediaCount,
    mediaTypes: facts.mediaTypes as ContentProfile['mediaTypes'],
    hasVideo: facts.hasVideo,
    hasPoster: facts.hasPoster,
    infoWeight: facts.infoWeight as ContentProfile['infoWeight'],
    hasDescription: facts.hasDescription,
    state: facts.state,
    actionCount: facts.actionCount,
    context: facts.context,
  };
}

/** Convierte la config PIE a la config VCE (solo campos compatibles). */
function toVceConfig(config: TenantPieConfig) {
  return {
    fitPolicy: 'contain' as const,
    mediaRatios: { default: '3/4', heavy: '1/1' },
    capabilities: config.capabilities,
    infoTreatment: { clampLines: 3, expandable: true },
    presentation: {
      mediaBgClass: config.aesthetics?.mediaBgClass ?? 'bg-gray-100',
      interactive: config.aesthetics?.interactive ?? true,
      mediaContainerClass: config.aesthetics?.mediaContainerClass,
    },
    texts: config.texts,
  };
}

// ── VCE-BASELINE: el VCE actual como candidato de referencia ──
// Garantía anti-regresión (ratificado): el motor solo cambia cuando
// un candidato supera al baseline según los goals del tenant.
export const vceBaselineStrategy: Strategy = {
  id: 'vce-baseline',
  generate(facts) {
    const profile = toVceProfile(facts);
    const d = composeDecision(profile, toVceConfig({ goals: [] }));
    return [{
      strategyId: 'vce-baseline',
      frame: { ratio: d.mediaRatio },
      placement: { fit: d.fit, align: 'center' },
      environment: { backdrop: 'none' },
      capabilities: d.capabilities,
      infoTreatment: d.infoTreatment,
      actionPriority: d.actionPriority,
      variant: d.variant,   // propaga la variante del SmartCard (VCE)
    }];
  },
};

// ── CANON-RATIO: frame con el ratio canónico del tenant ──
// Genera un candidato por cada ratio permitido (canon del catálogo).
// La consistencia (O2) se puntúa en el optimizer; aquí solo se propone.
export const canonRatioStrategy: Strategy = {
  id: 'canon-ratio',
  generate(facts, _ctx, _intent, config) {
    const allowed = config.constraints?.allowedRatios ?? ['3/4', '1/2'];
    return allowed.map((ratio) => ({
      strategyId: 'canon-ratio',
      frame: { ratio },
      placement: { fit: 'contain' as const, align: 'center' },
      environment: { backdrop: 'none' as const, padding: 0.04 },
      capabilities: {},
      infoTreatment: facts.infoWeight === 'heavy' ? ('clamped' as const) : ('compact' as const),
      actionPriority: facts.state === 'blocked' || facts.state === 'sold' || facts.state === 'verifying'
        ? ('prominent' as const) : ('normal' as const),
    }));
  },
};

// ── REAL-FIT: frame con el aspect REAL de la foto ──
// Desperdicio ~0 (O1). Declina si no hay realAspect (facts opcional).
export const realFitStrategy: Strategy = {
  id: 'real-fit',
  generate(facts) {
    if (!facts.realAspect) return [];
    // Traduce el aspect numérico a un token de ratio legible
    const a = facts.realAspect;
    let token: string;
    if (a <= 0.55) token = '1/2';
    else if (a <= 0.85) token = '3/4';
    else if (a <= 1.15) token = '1/1';
    else if (a <= 1.6) token = '4/3';
    else token = '16/9';
    return [{
      strategyId: 'real-fit',
      frame: { ratio: token },
      placement: { fit: 'contain' as const, align: 'center' },
      environment: { backdrop: 'none' as const, padding: 0.02 },
      capabilities: {},
      infoTreatment: facts.infoWeight === 'heavy' ? ('clamped' as const) : ('compact' as const),
      actionPriority: facts.state === 'blocked' || facts.state === 'sold' || facts.state === 'verifying'
        ? ('prominent' as const) : ('normal' as const),
    }];
  },
};

// ── EXPAND-BACKDROP: frame canónico + fondo expandido ──
// DECLARADA en I1 (el render del backdrop llega en I3). Genera el
// candidato si el tenant configura expandStyle; la invariante I2/I3
// valida autenticidad (requiere subjectBox para modos generativos).
export const expandBackdropStrategy: Strategy = {
  id: 'expand-backdrop',
  generate(facts, _ctx, _intent, config) {
    const style = config.constraints?.expandStyle;
    if (!style) return [];
    const allowed = config.constraints?.allowedRatios ?? ['3/4'];
    return allowed.map((ratio) => ({
      strategyId: 'expand-backdrop',
      frame: { ratio },
      placement: { fit: 'contain' as const, align: 'center' },
      environment: {
        backdrop: 'expand' as const,
        expand: { mode: style.mode, fallbackColor: style.fallbackColor },
        padding: 0.04,
      },
      capabilities: {},
      infoTreatment: facts.infoWeight === 'heavy' ? ('clamped' as const) : ('compact' as const),
      actionPriority: facts.state === 'blocked' || facts.state === 'sold' || facts.state === 'verifying'
        ? ('prominent' as const) : ('normal' as const),
    }));
  },
};

// ── INFO-AWARE: refina infoTreatment/actionPriority (hereda F3) ──
// No propone geometría: ajusta la capa de información de cualquier
// candidato. Se combina en el optimizer (multiplicidad de candidatos).
export const infoAwareStrategy: Strategy = {
  id: 'info-aware',
  generate(facts) {
    return [{
      strategyId: 'info-aware',
      frame: { ratio: facts.realAspect ? 'real' : '3/4' },
      placement: { fit: 'contain' as const, align: 'center' },
      environment: { backdrop: 'none' as const, padding: 0.03 },
      capabilities: {},
      infoTreatment: facts.infoWeight === 'heavy' ? ('clamped' as const) : ('full' as const),
      actionPriority: facts.state === 'blocked' || facts.state === 'sold' || facts.state === 'verifying'
        ? ('prominent' as const) : ('normal' as const),
    }];
  },
};

export const STRATEGY_REGISTRY: Record<string, Strategy> = {
  'vce-baseline': vceBaselineStrategy,
  'canon-ratio': canonRatioStrategy,
  'real-fit': realFitStrategy,
  'expand-backdrop': expandBackdropStrategy,
  'info-aware': infoAwareStrategy,
};
